import {
  useState,
  ReactNode,
  ReactElement,
  useEffect,
  useCallback,
} from 'react'
import { useRouter } from 'next/router'
import { animateScroll } from 'react-scroll'

import { createCtx } from '@/lib/utils/context'
import { checkNotEmpty } from '@/lib/utils'
import { SCROLL_OPTION } from '@/lib/scroll'

type Datum = string | string[] | undefined

type DataType = Record<string, Datum>

type QueryParamsContext = {
  query: DataType
  setQuery: SetQueryFunction
  setQueries: SetQueriesFunction
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}

type SetQueryFunction = (key: string, value: Datum) => void
type SetQueriesFunction = (meta: DataType) => void

type QueryParamsProviderProps = {
  children: ReactNode
  initialQuery?: DataType
  isChangeHistory?: boolean
  shallowRouteChange?: boolean
  scrollToTop?: boolean
  allowedKey?: string[]
}

const DEFAULT_ALLOWED_KEY = [
  'sort_by',
  'sort_direction',
  'page',
  'per_page',
  'search',
  'query',
]

const queryParamsContext = createCtx<QueryParamsContext>()
const Provider = queryParamsContext[1]

export const useQueryParamsContext = queryParamsContext[0]

export const QueryParamsContextProvider = ({
  children,
  initialQuery = {},
  isChangeHistory = true,
  shallowRouteChange = true,
  scrollToTop = true,
  allowedKey = DEFAULT_ALLOWED_KEY,
}: QueryParamsProviderProps): ReactElement => {
  const router = useRouter()

  const [state, setState] = useState<DataType>(() => ({
    ...initialQuery,
    ...router.query,
  }))

  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!shallowRouteChange) {
      const handleRouteChangeStart = () => {
        setIsLoading(true)
      }

      const handleRouteChangeDone = () => {
        setIsLoading(false)
      }

      router.events.on('routeChangeStart', handleRouteChangeStart)

      router.events.on('routeChangeError', handleRouteChangeDone)
      router.events.on('routeChangeComplete', handleRouteChangeDone)

      return () => {
        router.events.off('routeChangeStart', handleRouteChangeStart)

        router.events.off('routeChangeError', handleRouteChangeDone)
        router.events.off('routeChangeComplete', handleRouteChangeDone)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {}
  }, [shallowRouteChange])

  useEffect(() => {
    if (isChangeHistory) {
      const queryKeyValues = Object.entries({
        ...router.query,
        ...state,
      })
        .filter(([_, value]) => checkNotEmpty(value))
        .filter(([key, _]) => allowedKey.includes(key))

      const queryObject: DataType = {}

      queryKeyValues.forEach(([key, value]) => {
        queryObject[key] = value
      })

      router.replace(
        {
          pathname: router.pathname,
          query: queryObject,
        },
        undefined,
        {
          shallow: shallowRouteChange,
          scroll: false,
        },
      )
    }
  }, [state])

  useEffect(() => {
    if (scrollToTop) {
      animateScroll.scrollToTop(SCROLL_OPTION)
    }
  }, [state])

  const setQuery = useCallback<SetQueryFunction>((key, value) => {
    setState((curState) => ({
      ...curState,
      [key]: value,
    }))
  }, [])

  const setQueries = useCallback<SetQueriesFunction>((meta) => {
    setState((curState) => {
      return { ...curState, ...meta }
    })
  }, [])

  const contextValue = {
    query: state,
    setQuery,
    setQueries,
    isLoading,
    setIsLoading,
  }

  return <Provider value={contextValue}>{children}</Provider>
}
